<template>
  <AmLoading 
    v-if="loading"
    :isLoading="loading"
    class="mx-auto pb-10"
  />
  <div
    v-else
    :class="['config-app mb-10', {
      'w-[96%]': mq.current === 'mobile',
      'max-w-[85%]': mq.current === 'tablet'
    }]"
  >
    <AmToast
      v-if="showAlert"
      :message="alertMessage"
      :duration="3000"
      margin="mt-28 ml-4"
      position="top-right"
      closable
    />
    <AmTypography
      v-if="!loading && appStore.screenMode === ScreenMode.CREATION || appStore.screenMode === ScreenMode.DRAFT"
      :text="$t(`createAppForm.configApp.description`)"
      class="create-app-form__description max-w-2xl text-center mb-[30px]"
      typographyStyle="l5"
      textColor="text100"
    />
    <div
      v-if="isEditMode"
      class="grid grid-rows-2 mb-6 md:grid-rows-none md:grid-cols-2 gap-8"
    >
      <div>
        <AmInput
          v-model="state.host"
          :label="$t('configApp.inputs.host.title')"
          :placeholder="$t('configApp.inputs.host.placeholder')"
          size="fullWidth"
          feedbackType="error"
        />
        <span class="text-xs text-roboto text-text80">{{$t('configApp.inputs.host.description')}}</span>
      </div>

      <div>
        <AmInput
          v-model="state.systemURL"
          :label="$t('configApp.inputs.systemURL.title')"
          :placeholder="$t('configApp.inputs.systemURL.placeholder')"
          size="fullWidth"
          feedbackType="error"
        />
        <span class="text-xs text-roboto text-text80">{{$t('configApp.inputs.systemURL.description')}}</span>
      </div>

      <div>
        <AmInput
          v-model="state.validationURL"
          :label="$t('configApp.inputs.validationURL.title')"
          :placeholder="$t('configApp.inputs.validationURL.placeholder')"
          size="fullWidth"
          feedbackType="error"
        />
        <span class="text-xs text-roboto text-text80">{{$t('configApp.inputs.validationURL.description')}}</span>
      </div>
    </div>
    <div
      v-if="options.length > 0"
      class="config-app_select-all"
    >
      <AmCheckbox
        v-model="allSelected"
        :label="$t('configApp.actions.selectAllPermissions')"
        data-test="config-app-permission-select-all"
        dataTestName="config-app-permission-select-all"
      />
    </div>
    <div
      :class="[
        'config-app_group-checkboxes',
        {
          'config-app_group-checkboxes--default columns-2 gap-8': mq.current !== 'mobile',
          'config-app_group-checkboxes--mobile': mq.current === 'mobile'
        }
      ]"
    >
      <AmCheckbox
        v-for="option in options"
        :key="option.id"
        v-model="option.checked"
        dataTestName="config-app-checkbox"
        :label="$t(`configApp.permissions.${option.label}`)"
        :value="option.value"
        :class="['config-app_checkbox', {
          'mb-3': mq.current !== 'mobile'
        }]"
        @click="addPermission(option.value)"
      />
    </div>

    <div
      v-if="appStore.screenMode === ScreenMode.CREATION || appStore.screenMode === ScreenMode.DRAFT"
      :class="[
        'config-app_buttons',
        {
          'config-app_buttons--default': mq.current !== 'mobile',
          'config-app_buttons--mobile': mq.current === 'mobile'
        }
      ]"
    >
      <AmButton
        variant="maxGhost"
        dataTestName="cancel-config-app-button"
        :label="isEditMode ? $t('configApp.actions.cancel') : $t(`configApp.actions.return`)"
        :size="mq.current === 'mobile' ? 'fullWidth' : 'xxlarge'"
        :class="[{ 'py-3': mq.current === 'mobile' }]"
        @click="isEditMode ? cancelEdit() : ActionBackToImageApp()"
      />

      <AmButton
        variant="maxPrimary"
        dataTestName="submit-config-app-button"
        :label="isEditMode ? $t('configApp.actions.saveAndSendToAnalysis') : $t('configApp.actions.saveConfig')"
        :size="mq.current === 'mobile' ? 'fullWidth' : 'xxlarge'"
        :disabled="!formIsValid"
        :class="[{ 'py-3': mq.current === 'mobile' }]"
        @eventKeyAmButton="submitForm"
        @click="submitForm"
      />
    </div>
  </div>
</template>

<script setup>
import { AmCheckbox, AmTypography, AmButton, AmLoading, AmInput, AmToast } from '@appmax_npm/ds-appmax-v3'
import { computed, inject, onBeforeUnmount, onBeforeMount, reactive, ref, watch, onUnmounted } from 'vue'
import { useAppsStore } from '@/stores/apps';
import _ from 'lodash'
import { useRouter } from 'vue-router';
import { useHttpStore } from '../../../stores/http';
import { useI18n } from 'vue-i18n';
import { useAppStore, ScreenMode } from '@/stores/modules/app'
import { useReturnBarStore } from '@/stores/returnBar'

const emit = defineEmits(['formSubmitIsFinish'])

const appsStore = useAppsStore()
const httpStore = useHttpStore()
const appStore = useAppStore()
const returnBarStore = useReturnBarStore()
const mq = inject('mq')
const router = useRouter()
let options = ref([])
const { t } = useI18n()
const appSelectedOptions = ref([])
const loading = ref(false)
const showAlert = ref(false)
const alertMessage = ref(null)
const state = reactive({
  host: '',
  systemURL: '',
  validationURL: ''
});
const data = {}

const formIsValid = computed(() => _.some(options.value, (option) => option.checked))
const isEditMode = computed(() => appStore.screenMode === ScreenMode.EDITING)

const allSelected = computed({
  get: () => options.value.length > 0 && options.value.every((option) => option.checked),
  set: (value) => {
    options.value.forEach((option) => {
      option.checked = value
    })
    addPermission()
  }
})

const getOptions = (async () => {
  loading.value = true
    
  await httpStore.getPermissions().then((response) => {
    options.value = _.map(response.data.data, (label, value) => ({ 
      label: _.camelCase(label),
      value: value,
      checked: false
    }))

    loading.value = false
  }).catch((error) => {
    console.log(error)
  })
})

const addPermission = () => {
  const selectedOptions = []

  _.forEach(options.value, (option) => {
    if (option.checked) {
      selectedOptions.push(option.value)
    }
  })

  appStore.app.permissions = selectedOptions
  appStore.validateEditForm = formIsValid.value
  appStore.modificatedSteps = appStore.addStepModificated('configApp')
}

const setExistingData = () => {
  loading.value = true

  const { id, host, systemURL, validationURL } = appStore.app

  appsStore.setAppID(id)
  state.host = host
  state.systemURL = systemURL
  state.validationURL = validationURL

  appSelectedOptions.value = _.map(appStore.app.permissions, item => _.pick(item, ['id', 'label']))

  _.forEach(options.value, (option) => {
    _.forEach(appSelectedOptions.value, (appOption) => {
      if (_.camelCase(appOption.label) === option.label) {
        option.checked = true
      }
    })
  })

  data.id = id
  loading.value = false
}

onBeforeMount(async () => {
  await getOptions()
    
  if (appStore.screenMode === ScreenMode.DRAFT || appStore.screenMode === ScreenMode.CREATION) {
    returnBarStore.title = t(`createAppForm.configApp.title`)
  }

  if (appStore.screenMode === ScreenMode.EDITING) {
    setExistingData()
    return
  }

  data.app_id = appsStore.appID
})

watch(
  () => state.host, (value) => {
    addPermission()
    appStore.app.host = value
  })

watch(
  () => state.systemURL, (value) => {
    addPermission()
    appStore.app.systemURL = value
  })

watch(
  () => state.validationURL, (value) => {
    addPermission()
    appStore.app.validationURL = value
  })

const cancelEdit = () => {
  emit('formSubmitIsFinish', false);
}

const submitForm = async () => {
  const selectedOptions = []
  loading.value = true

  _.forEach(options.value, (option) => {
    if (option.checked) {
      selectedOptions.push(option.value)
    }
  })

  if (appStore.screenMode === ScreenMode.CREATION || appStore.screenMode === ScreenMode.DRAFT) {
    data.permissions = selectedOptions

    httpStore.sendConfigToApp(appStore.app.id, data).then(() => {
      // Fase 1.2: marcar flag para exibir toast informativo na listagem
      appStore.justConfiguredAppId = appStore.app.id
      router.push({
        name: 'app-status',
        params: {
          id: appStore.app.id
        }
      })
    }).catch(() => {
      loading.value = false
      showAlert.value = true

      alertMessage.value = {
        alertType: 'warning',
        title: computed(() => t('configApp.feedbacks.form.error.title')),
        description: computed(() =>
          t('configApp.feedbacks.form.error.description')
        )
      }
    })

    return
  }

  data.permissions = selectedOptions
  data.url_repository = state.systemURL
  data.app_external_host = state.host
  data.app_external_validation = state.validationURL

  httpStore.editConfigApp(appsStore.appID, data).then(() => {
    emit('formSubmitIsFinish', false);

    router.push({
      name: 'app-status',
      params: {
        id: appsStore.appID
      }
    })
  }).catch(() => {
    emit('formSubmitIsFinish', true);
    loading.value = false
  })
}

const ActionBackToImageApp = () => {
  router.push({ name: 'create-app_images-app', state: { parameter: true } });
}

onBeforeUnmount(() => {
  appsStore.appID = null
})

onUnmounted(() => {
  if (appStore.screenMode !== ScreenMode.EDITING) {
    returnBarStore.title = ''
  }
})
</script>

<style lang="scss" scoped>
    .config-app {
        &_select-all {
            padding-bottom: 14px;
            margin-bottom: 14px;
            border-bottom: 1px solid var(--surface-200, #e5e7eb);

            .am-v3-checkbox-v3-label {
              font-weight: 500 !important;
            }
        }

        &_checkbox {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            color: #333;
            break-inside: avoid;
        }
        &_group-checkboxes {
            &--default {
                margin: 10px 0 60px 0;

                & > * {
                  margin-right: 40px;
                }
            }

            &--mobile {
                display: flex;
                flex-direction: column;
                margin: 0 20px 24px 20px;
                gap: 24px;
            }
        }

        &_buttons {
            &--default {
                display: flex;
                justify-content: flex-end;
                flex-direction: row;
                gap: 10px;
            }

            &--mobile {
                display: flex;
                flex-direction: column-reverse;
                gap: 24px;
            }
        }
    }
</style>